// Create / edit a Learning article. Markdown body with a live preview.
// Attachments (files + inline images) are managed on the article page itself.
import { useState } from "react";
import { Loader2, X, Eye, Pencil } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import Markdown from "@/components/Markdown";
import { ICON_CHOICES } from "@/pages/learning/iconMap";

const BRAND = "var(--brand-accent, #C75A1B)";

const AUDIENCES = [
    { value: "all", label: "Everyone (portal + Priority Risk)" },
    { value: "client", label: "Client portal only" },
    { value: "pr", label: "Priority Risk staff only" },
];

const FIELD = "w-full h-9 px-2.5 rounded-lg border text-[13px]";

export default function ArticleEditorDialog({ article, onClose, onSaved }) {
    const editing = !!article;
    const [form, setForm] = useState({
        title: article?.title || "",
        category: article?.category || "General",
        summary: article?.summary || "",
        audience: article?.audience || "client",
        status: article?.status || "published",
        icon: article?.icon || "BookOpen",
        sort_order: article?.sort_order ?? 0,
        body: article?.body || "",
    });
    const [saving, setSaving] = useState(false);
    const [tab, setTab] = useState("write"); // write | preview
    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const save = async () => {
        if (!form.title.trim()) { toast.error("Give the guide a title."); return; }
        setSaving(true);
        try {
            const payload = { ...form, sort_order: Number(form.sort_order) || 0 };
            const resp = editing
                ? await api.patch(`/learning/articles/${article.id}`, payload)
                : await api.post("/learning/articles", payload);
            toast.success(editing ? "Guide updated." : "Guide created.");
            onSaved(resp.data);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Could not save the guide.");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" data-testid="learning-editor-dialog" onClick={onClose}>
            <div className="bg-white rounded-[14px] w-full max-w-3xl shadow-2xl overflow-hidden flex flex-col max-h-[92vh]" onClick={(e) => e.stopPropagation()}>
                <div className="px-5 py-3.5 border-b flex items-center gap-2" style={{ borderColor: "#F0EDE8" }}>
                    <h3 className="text-[14px] font-semibold flex-1">{editing ? "Edit guide" : "New guide"}</h3>
                    <button data-testid="learning-editor-close" onClick={onClose}><X className="w-4 h-4 text-[#9A968F]" /></button>
                </div>

                <div className="p-5 space-y-3.5 overflow-y-auto">
                    <div className="grid sm:grid-cols-2 gap-3">
                        <label className="block text-[12px] font-semibold text-[#1F1F1F] sm:col-span-2">Title
                            <input data-testid="learning-field-title" className={FIELD} style={{ borderColor: "#E5E0DA" }}
                                value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="e.g. Signing in and out of site" />
                        </label>
                        <label className="block text-[12px] font-semibold text-[#1F1F1F]">Category
                            <input data-testid="learning-field-category" className={FIELD} style={{ borderColor: "#E5E0DA" }}
                                value={form.category} onChange={(e) => set("category", e.target.value)} placeholder="e.g. On Site" />
                        </label>
                        <label className="block text-[12px] font-semibold text-[#1F1F1F]">Icon
                            <select data-testid="learning-field-icon" className={FIELD} style={{ borderColor: "#E5E0DA" }}
                                value={form.icon} onChange={(e) => set("icon", e.target.value)}>
                                {ICON_CHOICES.map((c) => <option key={c} value={c}>{c}</option>)}
                            </select>
                        </label>
                        <label className="block text-[12px] font-semibold text-[#1F1F1F]">Who can see it
                            <select data-testid="learning-field-audience" className={FIELD} style={{ borderColor: "#E5E0DA" }}
                                value={form.audience} onChange={(e) => set("audience", e.target.value)}>
                                {AUDIENCES.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
                            </select>
                        </label>
                        <label className="block text-[12px] font-semibold text-[#1F1F1F]">Status
                            <select data-testid="learning-field-status" className={FIELD} style={{ borderColor: "#E5E0DA" }}
                                value={form.status} onChange={(e) => set("status", e.target.value)}>
                                <option value="published">Published (live)</option>
                                <option value="draft">Draft (hidden from readers)</option>
                            </select>
                        </label>
                        <label className="block text-[12px] font-semibold text-[#1F1F1F] sm:col-span-2">Summary
                            <input data-testid="learning-field-summary" className={FIELD} style={{ borderColor: "#E5E0DA" }}
                                value={form.summary} onChange={(e) => set("summary", e.target.value)} placeholder="One-line description shown on the card" />
                        </label>
                    </div>

                    <div>
                        <div className="flex items-center justify-between mb-1.5">
                            <span className="text-[12px] font-semibold text-[#1F1F1F]">Content <span className="font-normal text-[#9A968F]">(Markdown — # headings, - bullets, 1. steps, **bold**)</span></span>
                            <div className="flex rounded-lg border overflow-hidden text-[11.5px]" style={{ borderColor: "#E5E0DA" }}>
                                <button type="button" data-testid="learning-tab-write" onClick={() => setTab("write")}
                                    className={`inline-flex items-center gap-1 px-2.5 py-1 ${tab === "write" ? "text-white" : "text-[#6F6A64]"}`}
                                    style={tab === "write" ? { background: BRAND } : undefined}><Pencil size={12} /> Write</button>
                                <button type="button" data-testid="learning-tab-preview" onClick={() => setTab("preview")}
                                    className={`inline-flex items-center gap-1 px-2.5 py-1 ${tab === "preview" ? "text-white" : "text-[#6F6A64]"}`}
                                    style={tab === "preview" ? { background: BRAND } : undefined}><Eye size={12} /> Preview</button>
                            </div>
                        </div>
                        {tab === "write" ? (
                            <textarea data-testid="learning-field-body" value={form.body} onChange={(e) => set("body", e.target.value)}
                                rows={14} spellCheck className="w-full px-3 py-2.5 rounded-lg border text-[13px] font-mono leading-relaxed resize-y"
                                style={{ borderColor: "#E5E0DA" }} placeholder={"## Step one\n1. Do this\n2. Then this\n\nInsert an uploaded image with ![alt](url) — upload images on the guide page."} />
                        ) : (
                            <div className="border rounded-lg px-4 py-3 min-h-[14rem] bg-[#FCFBFA]" style={{ borderColor: "#E5E0DA" }}>
                                <Markdown text={form.body || "_Nothing to preview yet._"} />
                            </div>
                        )}
                    </div>
                </div>

                <div className="px-5 py-3.5 border-t flex items-center justify-end gap-2" style={{ borderColor: "#F0EDE8" }}>
                    <button data-testid="learning-editor-cancel" onClick={onClose} className="px-3.5 py-2 rounded-[8px] text-[12.5px] font-semibold border" style={{ borderColor: "#E5E0DA" }}>Cancel</button>
                    <button data-testid="learning-editor-save" onClick={save} disabled={saving}
                        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-[8px] text-[12.5px] font-semibold text-white disabled:opacity-60" style={{ background: BRAND }}>
                        {saving ? <Loader2 size={14} className="animate-spin" /> : null} {editing ? "Save changes" : "Create guide"}
                    </button>
                </div>
            </div>
        </div>
    );
}
